import { useState, useEffect, useCallback, useRef } from "react";
import { motion } from "framer-motion";
import { Calendar } from "@/components/ui/calendar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { CalendarDays, User, CheckCircle, CreditCard } from "lucide-react";
import axios from "axios";
import { trackEvent, metaTrack } from "@/lib/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// US phone auto-formatter. Returns the visually-formatted string like "(313) 484-7307".
const formatPhone = (raw) => {
  const digits = (raw || "").replace(/\D/g, "").slice(0, 10);
  if (digits.length === 0) return "";
  if (digits.length <= 3) return `(${digits}`;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
};

// localStorage key for autosaved booking progress
const BOOKING_DRAFT_KEY = "amb_booking_draft_v1";

const steps = [
  { id: 1, label: "Style", icon: CalendarDays },
  { id: 2, label: "Date", icon: CalendarDays },
  { id: 3, label: "Info", icon: User },
  { id: 4, label: "Deposit", icon: CreditCard },
];

const BookingSection = () => {
  const [step, setStep] = useState(1);
  const [services, setServices] = useState([]);
  const [selectedService, setSelectedService] = useState("");
  const [selectedDate, setSelectedDate] = useState(undefined);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [selectedTime, setSelectedTime] = useState("");
  const [formData, setFormData] = useState({
    client_name: "",
    client_email: "",
    client_phone: "",
    notes: "",
  });
  const [loading, setLoading] = useState(false);
  const [bookingId, setBookingId] = useState(null);
  const [payingDeposit, setPayingDeposit] = useState(false);
  const [referredBy, setReferredBy] = useState("");
  const [hairType, setHairType] = useState(""); // "natural" | "boho" — only asked when the selected service contains "boho"
  const formRef = useRef(null);

  useEffect(() => {
    // Capture referral from URL
    const params = new URLSearchParams(window.location.search);
    const ref = params.get("ref") || new URLSearchParams(window.location.hash.split("?")[1] || "").get("ref");
    if (ref) setReferredBy(ref.replace(/_/g, " "));
  }, []);

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const res = await axios.get(`${API}/services`);
        setServices(res.data);
      } catch (err) {
        // silently fail
      }
    };
    fetchServices();
  }, []);

  // Restore booking draft from localStorage on mount (only if very recent)
  useEffect(() => {
    try {
      const raw = localStorage.getItem(BOOKING_DRAFT_KEY);
      if (!raw) return;
      const draft = JSON.parse(raw);
      const ageMin = (Date.now() - (draft._savedAt || 0)) / 60000;
      if (ageMin > 60) {
        localStorage.removeItem(BOOKING_DRAFT_KEY);
        return;
      }
      if (draft.formData) setFormData((f) => ({ ...f, ...draft.formData }));
    } catch (e) { console.warn("Could not restore booking draft:", e); }
  }, []);

  // Persist form data as the user types
  useEffect(() => {
    try {
      if (!formData.client_name && !formData.client_email && !formData.client_phone) return;
      localStorage.setItem(BOOKING_DRAFT_KEY, JSON.stringify({
        formData,
        _savedAt: Date.now(),
      }));
    } catch (e) { console.warn("Could not save booking draft:", e); }
  }, [formData]);

  const fetchSlots = useCallback(async (date) => {
    const dateStr = date.toISOString().split("T")[0];
    try {
      const res = await axios.get(`${API}/available-slots?date=${dateStr}`);
      setAvailableSlots(res.data.slots);
    } catch (err) {
      console.error("Failed to load available slots:", err);
    }
  }, []);

  useEffect(() => {
    if (!selectedDate) return;
    fetchSlots(selectedDate);
  }, [selectedDate, fetchSlots]);

  const serviceName = services.find((s) => s.id === selectedService)?.name || "";
  const isBohoService = serviceName.toLowerCase().includes("boho");
  const serviceObj = services.find((s) => s.id === selectedService);

  const scrollToForm = () => {
    setTimeout(() => {
      formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 100);
  };

  // Step 1 → 2: Select style, auto-advance to calendar
  const handleSelectService = (svcId) => {
    setSelectedService(svcId);
    setSelectedDate(undefined);
    setSelectedTime("");
    setAvailableSlots([]);
    const svc = services.find((s) => s.id === svcId);
    trackEvent("view_item", { item_id: svcId, item_name: svc?.name || svcId, item_category: "Braiding Service", price: svc?.starting_price, currency: "USD" });
    // Fire BookingStarted event ONCE per session for both Microsoft Ads + Google Ads
    try {
      if (!sessionStorage.getItem("amb_booking_started_fired")) {
        if (window.uetq) {
          window.uetq.push("event", "BookingStarted", {
            event_category: "Booking",
            event_label: "Service selected",
          });
        }
        if (typeof window.gtag === "function") {
          window.gtag("event", "begin_checkout", { event_category: "Booking", event_label: "Service selected" });
        }
        if (typeof window.fbq === "function") {
          const metaEventId = window.crypto?.randomUUID ? window.crypto.randomUUID() : `ic-${Date.now()}`;
          window.fbq("track", "InitiateCheckout", { content_category: "Hair Braiding Service" }, { eventID: metaEventId });
          metaTrack("InitiateCheckout", metaEventId);
        }
        sessionStorage.setItem("amb_booking_started_fired", "1");
      }
    } catch (e) { console.warn("Analytics event failed:", e); }
    setStep(2);
    scrollToForm();
  };

  // Preselect a service when arriving from a lookbook/portfolio photo link (?service=svc-xxx)
  useEffect(() => {
    if (!services.length || selectedService) return;
    const params = new URLSearchParams(window.location.search);
    const svcId = params.get("service");
    if (svcId && services.some((s) => s.id === svcId)) {
      handleSelectService(svcId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [services]);

  // Step 2: Select date, then time, auto-advance to info
  const handleSelectTime = (slot) => {
    setSelectedTime(slot);
    trackEvent("add_to_cart", { item_id: selectedService, item_name: serviceName, value: 25, currency: "USD" });
    setStep(3);
    scrollToForm();
  };

  const handleSubmit = async () => {
    if (!formData.client_name || !formData.client_email || !formData.client_phone) {
      toast.error("Please fill in all required fields");
      return;
    }
    if (isBohoService && !hairType) {
      toast.error("Please choose Natural Hair or Boho Hair for your Boho braids");
      return;
    }
    setLoading(true);
    try {
      const dateStr = selectedDate.toISOString().split("T")[0];
      const res = await axios.post(`${API}/bookings`, {
        service_id: selectedService,
        service_name: serviceName,
        date: dateStr,
        time: selectedTime,
        referred_by: referredBy,
        promo_source: new URLSearchParams(window.location.search).get("promo") || "",
        hair_type: isBohoService ? hairType : "",
        ...formData,
      });
      setBookingId(res.data.id);
      trackEvent("booking_submitted", { item_id: selectedService, item_name: serviceName });
      try { localStorage.removeItem(BOOKING_DRAFT_KEY); } catch (e) { console.warn("Could not clear booking draft:", e); }
      toast.success("Appointment created! Pay $25 deposit to confirm.");
      setStep(4);
      scrollToForm();
    } catch (err) {
      toast.error("Failed to book. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handlePayDeposit = async () => {
    setPayingDeposit(true);
    trackEvent("begin_checkout", { item_id: selectedService, item_name: serviceName, value: 25, currency: "USD" });
    try {
      const origin = window.location.origin;
      const res = await axios.post(`${API}/payments/create-deposit`, {
        booking_id: bookingId,
        origin_url: origin,
      });
      // Remember session_id so we can notify the backend if the user cancels on Stripe
      try { sessionStorage.setItem("amb_pending_session_id", res.data.session_id); } catch (e) { console.warn("Could not store session id:", e); }
      window.location.href = res.data.url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Payment failed. Try again.");
      setPayingDeposit(false);
    }
  };

  const resetBooking = () => {
    setStep(1);
    setSelectedService("");
    setSelectedDate(undefined);
    setSelectedTime("");
    setBookingId(null);
    setFormData({ client_name: "", client_email: "", client_phone: "", notes: "" });
    setHairType("");
  };

  const disabledDays = { before: new Date() };

  // Hide the fixed Wednesday promo banner while the user is completing checkout
  // (steps 3-5) so it never covers the Continue/Pay buttons on mobile.
  useEffect(() => {
    document.body.classList.toggle("amb-checkout", step >= 3);
    return () => document.body.classList.remove("amb-checkout");
  }, [step]);

  // Check for Stripe payment return
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("session_id");
    const payment = params.get("payment");
    if (sessionId && payment === "success") {
      const pollStatus = async (attempts = 0) => {
        if (attempts >= 10) {
          toast.error("Payment verification timed out. If you were charged, please contact us at (313) 484-7307.");
          return;
        }
        try {
          const res = await axios.get(`${API}/payments/status/${sessionId}`);
          if (res.data.payment_status === "paid") {
            toast.success("Deposit paid! Appointment confirmed.");
            trackEvent("purchase", { value: 25, currency: "USD", transaction_id: sessionId });
            try { sessionStorage.removeItem("amb_pending_session_id"); } catch (e) { console.warn("sessionStorage cleanup failed:", e); }
            // Fire Google Ads Purchase conversion
            if (typeof window.gtag === "function") {
              window.gtag("event", "conversion", {
                send_to: "AW-6255108652/WcksCO-VwJ4cEL7Qw9NC",
                value: 25.0,
                currency: "USD",
                transaction_id: sessionId,
              });
            }
            // Fire Microsoft Advertising (Bing UET) conversion
            if (window.uetq) {
              window.uetq.push("event", "DepositPaid", {
                event_category: "Booking",
                event_label: "Stripe deposit paid",
                event_value: 25,
                revenue_value: 25,
                currency: "USD",
              });
            }
            // Fire Meta Pixel (Facebook/Instagram) Purchase conversion
            if (typeof window.fbq === "function") {
              window.fbq("track", "Purchase", {
                value: 25.00,
                currency: "USD",
                content_name: "Hair Braiding Booking Deposit",
                content_category: "Booking",
                content_type: "product",
              }, { eventID: sessionId });
            }
            setStep(5);
            window.history.replaceState({}, "", window.location.pathname);
            return;
          } else if (res.data.payment_status === "expired") {
            toast.error("Payment failed or expired. Your booking was not confirmed. Please try again.");
            try { sessionStorage.removeItem("amb_pending_session_id"); } catch (e) { console.warn("sessionStorage cleanup failed:", e); }
            setStep(3);
            window.history.replaceState({}, "", window.location.pathname);
            return;
          }
        } catch (e) { console.warn("Payment status poll failed, retrying:", e); }
        setTimeout(() => pollStatus(attempts + 1), 2000);
      };
      pollStatus();
    } else if (payment === "cancelled") {
      // Notify backend so it can email client + admin with the failure reason
      let pendingSession = null;
      try { pendingSession = sessionStorage.getItem("amb_pending_session_id"); } catch (e) { console.warn("sessionStorage read failed:", e); }
      if (pendingSession) {
        axios.post(`${API}/payments/cancel-session`, { session_id: pendingSession })
          .catch((e) => console.error("Failed to notify cancel-session:", e))
          .finally(() => {
            try { sessionStorage.removeItem("amb_pending_session_id"); } catch (e) { console.warn("sessionStorage cleanup failed:", e); }
          });
      }
      toast.error("Payment cancelled. Your booking was not confirmed — check your email for next steps.");
      setStep(4);
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []);

  // Group services by category for the dropdown
  const servicesByCategory = {};
  services.forEach((s) => {
    const cat = s.category;
    if (!servicesByCategory[cat]) servicesByCategory[cat] = [];
    servicesByCategory[cat].push(s);
  });

  const categoryLabels = {
    braids: "Braids",
    twists: "Kinky Twist",
    crochet: "Crochet & Ponytails",
    locs: "Faux Locs",
    mens: "Men's Services",
    cornrows: "Cornrows",
    beauty: "Hair & Beauty Services",
    other: "Other Services",
  };

  return (
    <section id="booking" className="py-20 md:py-32 relative" data-testid="booking-section">
      {/* Background */}
      <div className="absolute inset-0">
        <img
          src="https://customer-assets.emergentagent.com/job_afro-braiding-pro/artifacts/14fnwz8c_IMG-20260414-WA0038%20%281%29.jpg"
          alt="Salon"
          className="w-full h-full object-cover opacity-20"
        />
        <div className="absolute inset-0 bg-brand-bg/90" />
      </div>

      <div className="relative z-10 max-w-lg mx-auto px-4 md:px-6">
        {/* Header */}
        <motion.div
          className="text-center mb-8"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <p className="text-xs uppercase tracking-[0.2em] font-sans text-brand-gold mb-3">
            Schedule Your Visit
          </p>
          <h2 className="font-serif text-3xl md:text-5xl text-brand-text tracking-tight">
            Book Now
          </h2>
          <div className="gold-line-long mx-auto mt-4" />

          {/* Quick Contact */}
          <div className="mt-5 flex flex-col sm:flex-row items-center justify-center gap-2">
            <a href="tel:+13134847307" data-testid="booking-phone-link"
              className="inline-flex items-center gap-2 px-4 py-2 border border-brand-gold text-brand-gold font-sans text-xs font-semibold hover:bg-brand-gold hover:text-black transition-colors">
              <svg viewBox="0 0 24 24" className="w-3.5 h-3.5 fill-current"><path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z"/></svg>
              (313) 484-7307
            </a>
            <a href="https://wa.me/13134847307?text=Hi%20Afro%20Magic!%20I'd%20like%20to%20book." target="_blank" rel="noopener noreferrer" data-testid="whatsapp-booking-btn"
              className="inline-flex items-center gap-2 px-4 py-2 bg-[#25D366] text-white font-sans text-xs font-semibold hover:bg-[#1da851] transition-colors">
              <svg viewBox="0 0 24 24" className="w-3.5 h-3.5 fill-current"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
              WhatsApp
            </a>
          </div>
        </motion.div>

        {/* Progress Steps */}
        <div className="flex items-center justify-center gap-1 mb-6" ref={formRef}>
          {steps.map((s, i) => {
            const isActive = step >= s.id;
            const isDone = step > s.id;
            return (
              <div key={s.id} className="flex items-center gap-1">
                <div className={`w-7 h-7 flex items-center justify-center text-[10px] font-sans font-bold transition-colors ${
                  isDone ? "bg-brand-gold text-black" : isActive ? "border border-brand-gold text-brand-gold" : "border border-brand-border text-brand-text-muted/40"
                }`}>
                  {isDone ? <CheckCircle size={12} /> : s.id}
                </div>
                <span className={`font-sans text-[10px] tracking-wide ${isActive ? "text-brand-gold" : "text-brand-text-muted/40"}`}>{s.label}</span>
                {i < steps.length - 1 && <div className={`w-6 h-px mx-1 ${step > s.id ? "bg-brand-gold" : "bg-brand-border"}`} />}
              </div>
            );
          })}
        </div>

        {/* Booking Card */}
        <div className="glass-card p-5 md:p-8" data-testid="booking-form">

          {/* Referral Banner */}
          {referredBy && (
            <div className="bg-brand-gold/10 border border-brand-gold/30 px-4 py-3 mb-4 text-center" data-testid="referral-banner">
              <p className="font-sans text-sm text-brand-gold font-semibold">Referred by {referredBy} — You get $10 OFF!</p>
              <p className="font-sans text-[10px] text-brand-text-muted">Valid on services $100 and above</p>
            </div>
          )}

          {/* STEP 1: Choose Style */}
          {step === 1 && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.2 }}>
              <h3 className="font-serif text-lg text-brand-text mb-4">Choose Your Style</h3>
              <Select value={selectedService} onValueChange={handleSelectService}>
                <SelectTrigger className="bg-brand-bg border-brand-border text-brand-text rounded-none h-14 text-base px-4" data-testid="booking-service-select">
                  <SelectValue placeholder="Tap to select a style..." />
                </SelectTrigger>
                <SelectContent className="max-h-80">
                  {Object.entries(servicesByCategory).map(([cat, svcs]) => (
                    <div key={cat}>
                      <div className="px-2 py-1.5 text-[10px] uppercase tracking-wider text-brand-text-muted font-semibold bg-brand-surface">{categoryLabels[cat] || cat}</div>
                      {svcs.map((svc) => (
                        <SelectItem key={svc.id} value={svc.id} className="py-2.5">
                          <div className="flex items-center justify-between w-full gap-3">
                            <span className="text-sm">{svc.name}</span>
                            <span className="text-xs text-brand-gold font-semibold">${svc.starting_price}</span>
                          </div>
                        </SelectItem>
                      ))}
                    </div>
                  ))}
                </SelectContent>
              </Select>

              {/* Quick preview of popular styles as buttons */}
              <p className="font-sans text-[10px] text-brand-text-muted mt-4 mb-2 uppercase tracking-wider">Popular Styles</p>
              <div className="grid grid-cols-2 gap-2">
                {services.filter(s => ["svc-knotless-braids-med-midback", "svc-box-braids-med-midback", "svc-fulani-braids-med-midback", "svc-boho-braids", "svc-womens-cornrows", "svc-mens-cornrows"].includes(s.id)).map((svc) => (
                  <button
                    key={svc.id}
                    onClick={() => handleSelectService(svc.id)}
                    data-testid={`booking-quick-${svc.id}`}
                    className="text-left p-3 border border-brand-border hover:border-brand-gold/50 transition-colors"
                  >
                    <p className="font-sans text-xs font-medium text-brand-text">{svc.name}</p>
                    <p className="font-sans text-[10px] text-brand-gold">${svc.starting_price}</p>
                  </button>
                ))}
              </div>
            </motion.div>
          )}

          {/* STEP 2: Date & Time */}
          {step === 2 && (
            <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.2 }}>
              {/* Selected service summary */}
              {serviceObj && (
                <div className="flex items-center justify-between p-3 border border-brand-gold/30 bg-brand-gold/5 mb-4">
                  <div>
                    <p className="font-sans text-sm font-medium text-brand-text">{serviceObj.name}</p>
                    <p className="font-sans text-[10px] text-brand-text-muted">{serviceObj.duration}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-sans text-sm text-brand-gold font-semibold">${serviceObj.starting_price}</p>
                    <button onClick={() => setStep(1)} className="font-sans text-[10px] text-brand-text-muted underline">Change</button>
                  </div>
                </div>
              )}

              <h3 className="font-serif text-lg text-brand-text mb-3">Pick a Date</h3>
              <div className="booking-calendar flex justify-center mb-4">
                <Calendar
                  mode="single"
                  selected={selectedDate}
                  onSelect={(d) => { setSelectedDate(d); setSelectedTime(""); }}
                  disabled={disabledDays}
                  data-testid="booking-calendar"
                  className="border border-brand-border bg-brand-bg p-2"
                />
              </div>

              {selectedDate && (
                <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
                  <h3 className="font-serif text-lg text-brand-text mb-2">Pick a Time</h3>
                  <p className="font-sans text-xs text-brand-text-muted mb-3">
                    {selectedDate.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" })}
                  </p>
                  <div className="grid grid-cols-3 gap-2">
                    {availableSlots.map((slot) => (
                      <button
                        key={slot}
                        onClick={() => handleSelectTime(slot)}
                        data-testid={`time-slot-${slot.replace(/\s/g, "-")}`}
                        className={`min-h-12 py-3 border font-sans text-sm text-center transition-all ${
                          selectedTime === slot
                            ? "border-brand-gold bg-brand-gold text-black font-semibold"
                            : "border-brand-border text-brand-text-muted hover:border-brand-gold/50 active:bg-brand-gold/10"
                        }`}
                      >
                        {slot}
                      </button>
                    ))}
                  </div>
                  {availableSlots.length === 0 && (
                    <p className="text-center text-sm text-brand-text-muted font-sans py-4">No slots available. Try another date.</p>
                  )}
                </motion.div>
              )}

              <button onClick={() => setStep(1)} className="w-full mt-4 py-2.5 border border-brand-border text-brand-text-muted font-sans text-xs">
                Back to Style Selection
              </button>
            </motion.div>
          )}

          {/* STEP 3: Contact Info */}
          {step === 3 && (
            <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.2 }}>
              {/* Summary bar */}
              <div className="p-3 border border-brand-gold/30 bg-brand-gold/5 mb-4">
                <p className="font-sans text-xs text-brand-text">
                  <span className="text-brand-gold font-semibold">{serviceName}</span>
                  {" — "}
                  {selectedDate?.toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                  {" at "}
                  <span className="text-brand-gold">{selectedTime}</span>
                </p>
              </div>

              <h3 className="font-serif text-lg text-brand-text mb-4">Your Information</h3>

              {/* Boho-only: choose Natural Hair vs Boho (extensions) */}
              {isBohoService && (
                <div
                  className="mb-4 p-4 border border-brand-gold/40 bg-brand-gold/5"
                  data-testid="booking-hair-type-selector"
                >
                  <p className="font-serif text-sm text-brand-text mb-1">Hair Choice for Boho <span className="text-red-400">*</span></p>
                  <p className="font-sans text-xs text-brand-text-muted mb-3">
                    Are you bringing your own hair, or would you like to use Boho (extension) hair? This helps us prep correctly for your appointment.
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <button
                      type="button"
                      onClick={() => setHairType("natural")}
                      data-testid="hair-type-natural-btn"
                      className={`p-3 border font-sans text-sm text-left transition-colors ${
                        hairType === "natural"
                          ? "border-brand-gold bg-brand-gold text-black font-semibold"
                          : "border-brand-border text-brand-text hover:border-brand-gold/60"
                      }`}
                    >
                      <span className="block font-semibold">Natural Hair</span>
                      <span className="block text-xs opacity-80 mt-0.5">Use my own hair</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => setHairType("boho")}
                      data-testid="hair-type-boho-btn"
                      className={`p-3 border font-sans text-sm text-left transition-colors ${
                        hairType === "boho"
                          ? "border-brand-gold bg-brand-gold text-black font-semibold"
                          : "border-brand-border text-brand-text hover:border-brand-gold/60"
                      }`}
                    >
                      <span className="block font-semibold">Boho Hair</span>
                      <span className="block text-xs opacity-80 mt-0.5">Use extensions</span>
                    </button>
                  </div>
                </div>
              )}

              <div className="space-y-3">
                <Input
                  value={formData.client_name}
                  onChange={(e) => setFormData({ ...formData, client_name: e.target.value })}
                  placeholder="Full Name *"
                  data-testid="booking-name-input"
                  autoComplete="name"
                  autoCapitalize="words"
                  className="bg-brand-bg border-brand-border text-brand-text placeholder:text-brand-text-muted/50 focus:border-brand-gold rounded-none h-14 text-base px-4"
                />
                <Input
                  type="email"
                  inputMode="email"
                  value={formData.client_email}
                  onChange={(e) => setFormData({ ...formData, client_email: e.target.value })}
                  placeholder="Email Address *"
                  data-testid="booking-email-input"
                  autoComplete="email"
                  autoCapitalize="none"
                  spellCheck={false}
                  className="bg-brand-bg border-brand-border text-brand-text placeholder:text-brand-text-muted/50 focus:border-brand-gold rounded-none h-14 text-base px-4"
                />
                <Input
                  type="tel"
                  inputMode="numeric"
                  value={formData.client_phone}
                  onChange={(e) => setFormData({ ...formData, client_phone: formatPhone(e.target.value) })}
                  placeholder="(313) 555-1234 *"
                  data-testid="booking-phone-input"
                  autoComplete="tel"
                  maxLength={14}
                  className="bg-brand-bg border-brand-border text-brand-text placeholder:text-brand-text-muted/50 focus:border-brand-gold rounded-none h-14 text-base px-4"
                />
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Special requests (optional)"
                  data-testid="booking-notes-input"
                  className="bg-brand-bg border-brand-border text-brand-text placeholder:text-brand-text-muted/50 focus:border-brand-gold rounded-none text-base min-h-[70px] px-4 py-3"
                />

                {/* SMS Consent */}
                <label className="flex items-start gap-3 cursor-pointer mt-2" data-testid="booking-sms-consent">
                  <input
                    type="checkbox"
                    checked={formData.sms_consent || false}
                    onChange={(e) => setFormData({ ...formData, sms_consent: e.target.checked })}
                    className="mt-1 w-5 h-5 accent-[#D4AF37] cursor-pointer flex-shrink-0"
                  />
                  <span className="font-sans text-xs text-brand-text-muted leading-relaxed">
                    I agree to receive automated appointment reminders and service updates via SMS from Afro Magic Braiding. Message and data rates may apply. Reply STOP to opt-out, HELP for help. <a href="/consent-policy" target="_blank" className="text-brand-gold underline">Privacy Policy</a>
                  </span>
                </label>

                {/* SMS Marketing Consent (optional) */}
                <label className="flex items-start gap-3 cursor-pointer mt-1" data-testid="booking-sms-marketing-consent">
                  <input
                    type="checkbox"
                    checked={formData.sms_marketing_consent || false}
                    onChange={(e) => setFormData({ ...formData, sms_marketing_consent: e.target.checked })}
                    className="mt-1 w-5 h-5 accent-[#D4AF37] cursor-pointer flex-shrink-0"
                  />
                  <span className="font-sans text-xs text-brand-text-muted leading-relaxed">
                    I agree to receive recurring appointment-related, promotional, and ecommerce text messages from Afro Magic Braiding Hair at the phone number provided. Messages may include service specials, new products, online-store offers, and other promotions. Consent is not a condition of purchase. Message and data rates may apply. Reply STOP to opt out.
                  </span>
                </label>
              </div>

              {/* Desktop action buttons — hidden on mobile (sticky bar handles mobile) */}
              <div className="hidden sm:flex gap-2 mt-5">
                <button onClick={() => setStep(2)} data-testid="booking-back-step-3"
                  className="flex-1 py-3 border border-brand-border text-brand-text-muted font-sans text-sm">Back</button>
                <button onClick={handleSubmit} disabled={loading} data-testid="booking-submit-btn"
                  className="flex-1 py-3 bg-brand-gold text-black font-sans font-semibold text-sm hover:bg-brand-gold-light transition-colors disabled:opacity-60">
                  {loading ? "Processing..." : "Continue to $25 Payment"}
                </button>
              </div>
              {/* Spacer so the sticky mobile bar doesn't overlap content */}
              <div className="sm:hidden h-24" />
            </motion.div>
          )}

          {/* STEP 4: Mandatory Deposit */}
          {step === 4 && (
            <motion.div initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.2 }}
              className="text-center py-4" data-testid="booking-deposit">
              <div className="w-14 h-14 mx-auto mb-4 border-2 border-brand-gold flex items-center justify-center">
                <CreditCard size={24} className="text-brand-gold" />
              </div>
              <h3 className="font-serif text-xl text-brand-text mb-2">Confirm Booking</h3>
              <p className="font-sans text-sm text-brand-text-muted mb-1">
                Pay <span className="text-brand-gold font-semibold">$25 deposit</span> to confirm your appointment.
              </p>
              <p className="font-sans text-xs text-brand-text-muted mb-5">
                Deposit is deducted from your total — pay the remaining balance at the shop.
              </p>

              {/* Apple Pay / Google Pay info badge */}
              <div className="flex items-center justify-center gap-2 mb-4 flex-wrap">
                <span className="font-sans text-[10px] text-brand-text-muted">Pay with</span>
                <span className="inline-flex items-center gap-1 bg-black text-white px-2 py-1 rounded text-[11px] font-semibold border border-white/20">
                  <svg viewBox="0 0 17 17" className="w-3 h-3 fill-current"><path d="M13.6 9c0-.7.3-1.3.8-1.7-.5-.7-1.2-1-2-1-1.2 0-2 .7-2.5.7s-1.4-.7-2.3-.7c-1.2 0-2.3.7-2.9 1.8-1.2 2.1-.3 5.3 1 7 .6.8 1.3 1.7 2.2 1.7.9 0 1.2-.6 2.3-.6s1.4.6 2.3.6 1.5-.8 2.1-1.6c.5-.7.9-1.6 1-2.5-1.1-.5-2-1.6-2-2.7zM12.1 4c.5-.6.9-1.4.8-2.2-.7 0-1.6.5-2.1 1.1-.5.5-.9 1.4-.8 2.1.8.1 1.6-.4 2.1-1z"/></svg>
                  Pay
                </span>
                <span className="inline-flex items-center gap-1 bg-white text-gray-800 px-2 py-1 rounded text-[11px] font-semibold border border-gray-300">
                  <span style={{color: "#4285F4"}}>G</span>
                  <span style={{color: "#EA4335"}}>P</span>
                  <span style={{color: "#FBBC05"}}>a</span>
                  <span style={{color: "#34A853"}}>y</span>
                </span>
                <span className="font-sans text-[10px] text-brand-text-muted">or any card</span>
              </div>

              <div className="p-3 border border-brand-border bg-brand-bg/50 mb-5 text-left">
                <p className="font-sans text-sm text-brand-text"><span className="text-brand-gold">{serviceName}</span></p>
                {isBohoService && hairType && (
                  <p className="font-sans text-xs text-brand-gold mt-0.5" data-testid="deposit-summary-hair-type">
                    Hair Type: <span className="font-semibold">{hairType === "natural" ? "Natural Hair" : "Boho Hair (extensions)"}</span>
                  </p>
                )}
                <p className="font-sans text-xs text-brand-text-muted">
                  {selectedDate?.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" })} at {selectedTime}
                </p>
                <p className="font-sans text-xs text-brand-text-muted">{formData.client_name} &middot; {formData.client_phone}</p>
                <div className="mt-2 pt-2 border-t border-brand-border">
                  <p className="font-sans text-lg text-brand-gold font-semibold">$25.00</p>
                </div>
              </div>

              <button onClick={handlePayDeposit} disabled={payingDeposit} data-testid="pay-deposit-btn"
                className="w-full py-4 bg-brand-gold text-black font-sans font-bold text-base hover:bg-brand-gold-light transition-colors disabled:opacity-60 min-h-[56px]">
                {payingDeposit ? "Redirecting..." : "Pay $25 & Confirm"}
              </button>
              <p className="font-sans text-[10px] text-brand-text-muted/60 mt-2">Apple Pay, Google Pay & cards accepted via Stripe</p>
            </motion.div>
          )}

          {/* STEP 5: Confirmed */}
          {step === 5 && (
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.3 }}
              className="text-center py-6" data-testid="booking-confirmation">
              <div className="w-14 h-14 mx-auto mb-4 border-2 border-brand-gold flex items-center justify-center">
                <CheckCircle size={28} className="text-brand-gold" />
              </div>
              <h3 className="font-serif text-xl text-brand-text mb-2">You're All Set!</h3>
              <p className="font-sans text-sm text-brand-text-muted max-w-sm mx-auto mb-6">
                Appointment confirmed! Check your email for details. See you soon!
              </p>
              <button onClick={resetBooking} data-testid="booking-new-btn"
                className="bg-brand-gold text-black font-sans font-semibold text-sm px-8 py-3 hover:bg-brand-gold-light transition-colors">
                Book Another
              </button>
            </motion.div>
          )}
        </div>
      </div>

      {/* Mobile sticky action bar — visible only on Step 3 on small screens */}
      {step === 3 && (
        <div
          className="sm:hidden fixed bottom-0 left-0 right-0 bg-brand-bg/95 backdrop-blur-md border-t border-brand-border z-[70]"
          style={{ paddingBottom: "max(env(safe-area-inset-bottom), 12px)" }}
          data-testid="booking-sticky-mobile-bar"
        >
          <div className="flex gap-2 px-4 pt-3">
            <button
              onClick={() => setStep(2)}
              data-testid="booking-back-mobile"
              className="px-5 py-3.5 border border-brand-border text-brand-text-muted font-sans text-sm min-h-[52px]"
            >
              Back
            </button>
            <button
              onClick={handleSubmit}
              disabled={loading}
              data-testid="booking-submit-mobile"
              className="flex-1 py-3.5 bg-brand-gold text-black font-sans font-bold text-base hover:bg-brand-gold-light transition-colors disabled:opacity-60 min-h-[52px]"
            >
              {loading ? "Processing..." : "Continue to Payment →"}
            </button>
          </div>
        </div>
      )}
    </section>
  );
};

export default BookingSection;
